import { useCallback, useMemo } from 'react';
import {
    FormContainer,
    PasswordElement,
    TextFieldElement,
} from 'react-hook-form-mui';
import Container from '@mui/material/Container';
import LoadingButton from '@mui/lab/LoadingButton';
import { useTranslation } from 'next-i18next';
import Typography from '@mui/material/Typography';
import { useMutation } from '@tanstack/react-query';
import ForgotPasswordButton from '@/components/Login/ForgotPasswordButton';
import jfLogo from '@/../public/img/jf-logo.png';

import getRoute, { RouteNames } from '@/common/utils/routes';
import useExtendedSnackbar from '@/hooks/useExtendedSnackbar';
import Image from 'next/image';
import { Box } from '@mui/system';
import useWasInitiallyAutofilled from '@/hooks/useWasInitiallyAutofilled';

type LoginFormProps = {
    // FIXME: should be one of available keys TFunction messes intellisense
    headerTranslationKey: any;
    fromClientZone?: boolean;
    fromAdmin?: boolean;
    successReloadRoute: RouteNames;
};

export default function LoginForm({
    headerTranslationKey,
    fromClientZone = false,
    fromAdmin = false,
    successReloadRoute,
}: LoginFormProps) {
    const { t } = useTranslation('common');
    const { enqueueSuccessSnackbar, enqueueErrorSnackbar, closeSnackbar } =
        useExtendedSnackbar();
    const wasInitiallyAutofilled = useWasInitiallyAutofilled();

    const { mutate, isLoading } = useMutation({
        mutationFn: async (body: any) =>
            fetch('/api/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            }).then((res) => {
                if (!res.ok) {
                    throw res.statusText;
                }

                return res.json();
            }),
    });

    const onSuccess = useCallback(
        async (body: any) => {
            closeSnackbar();
            mutate(body, {
                onSuccess: () => {
                    enqueueSuccessSnackbar(t('toast.successfullyLoggedIn'));

                    // Force page reload to correctly load cookies
                    window.location.href =
                        getRoute(successReloadRoute)?.pathname;
                },
                onError: () => {
                    enqueueErrorSnackbar(t('toast.errorWhenLoggingIn'));
                },
            });
        },
        [
            closeSnackbar,
            enqueueErrorSnackbar,
            enqueueSuccessSnackbar,
            mutate,
            successReloadRoute,
            t,
        ],
    );

    const defaultValues = useMemo(() => {
        return {
            username: '',
            password: '',
            fromClientZone,
            fromAdmin,
        };
    }, [fromAdmin, fromClientZone]);

    return (
        <Container
            sx={{
                width: '30%',
                margin: '0',
                height: '100dvh',
                marginLeft: '2.5rem',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    marginTop: '1rem',
                    marginBottom: '3rem',
                }}
            >
                <Image src={jfLogo} alt="jf sro logo" width={140} />
                <Typography
                    variant="h4"
                    sx={{
                        textWrap: 'balance',
                        color: '#302A58',
                        fontWeight: 600,
                    }}
                >
                    {t(headerTranslationKey)}
                </Typography>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1rem',
                    marginY: '1rem',
                }}
            >
                <Typography
                    variant="h2"
                    sx={{ fontWeight: 500, color: '#D94D4A' }}
                >
                    {t('login.welcome')}
                </Typography>
                <Typography variant="body1">{t('login.info')}</Typography>
            </Box>
            <FormContainer defaultValues={defaultValues} onSuccess={onSuccess}>
                <TextFieldElement
                    name="username"
                    label={t('login.username')}
                    fullWidth
                    required
                    margin="normal"
                    focused={wasInitiallyAutofilled ? true : undefined}
                />
                <PasswordElement
                    name="password"
                    label={t('login.password')}
                    fullWidth
                    required
                    margin="normal"
                    focused={wasInitiallyAutofilled ? true : undefined}
                />

                <LoadingButton
                    loading={isLoading}
                    type="submit"
                    variant="contained"
                    sx={{
                        marginTop: '1rem',
                        backgroundColor: '#D94D4A',
                        '&:hover': {
                            backgroundColor: '#D94D4A',
                        },
                        width: '100%',
                    }}
                >
                    {t('login.submit')}
                </LoadingButton>
                <ForgotPasswordButton />
            </FormContainer>
            <Typography
                sx={{
                    fontWeight: 600,
                    marginTop: '1.5rem',
                    whiteSpace: 'break-spaces',
                }}
            >
                {t('login.troubleLoggingIn')}
            </Typography>
        </Container>
    );
}
